import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";

// Lazy-loaded pages to reduce initial JS bundle
const Register = lazy(() => import("./pages/Register"));
const Opportunities = lazy(() => import("./pages/Opportunities"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Courses = lazy(() => import("./pages/Courses"));
const Vision = lazy(() => import("./pages/Vision"));
const BasicCourseBrochure = lazy(() => import("./pages/BasicCourseBrochure"));
const AwarenessTest = lazy(() => import("./pages/AwarenessTest"));
const Curriculum = lazy(() => import("./pages/Curriculum"));
const Pricing = lazy(() => import("./pages/Pricing"));
const SuccessStories = lazy(() => import("./pages/SuccessStories"));
const FreeWorkshop = lazy(() => import("./pages/FreeWorkshop"));
const VbaipEligibility = lazy(() => import("./pages/VbaipEligibility"));
const Services = lazy(() => import("./pages/Services"));
const JobPreparation = lazy(() => import("./pages/JobPreparation"));
const LearningLibrary = lazy(() => import("./pages/LearningLibrary"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const ProtectedRoute = lazy(() => import("./components/ProtectedRoute"));
const NotFound = lazy(() => import("./pages/NotFound"));
const DentalAI = lazy(() => import("./pages/DentalAI"));
const SummerCamp = lazy(() => import("./pages/SummerCamp"));
const SitemapPage = lazy(() => import("./pages/Sitemap"));
const DigitalMarketingGwalior = lazy(() => import("./pages/DigitalMarketingGwalior"));
const BlogEditor = lazy(() => import("./pages/BlogEditor"));
const BlogManage = lazy(() => import("./pages/BlogManage"));
const CoursePackages = lazy(() => import("./pages/CoursePackages"));
const PromptLibrary = lazy(() => import("./pages/PromptLibrary"));
const ThumbnailGenerator = lazy(() => import("./pages/ThumbnailGenerator"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/register" element={<Register />} />
            <Route path="/opportunities" element={<Opportunities />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/courses" element={<Courses />} />
            <Route path="/vision" element={<Vision />} />
            <Route path="/basic-course-brochure" element={<BasicCourseBrochure />} />
            <Route path="/awareness-test" element={<AwarenessTest />} />
            <Route path="/curriculum" element={<Curriculum />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/success-stories" element={<SuccessStories />} />
            <Route path="/free-workshop" element={<FreeWorkshop />} />
            <Route path="/vbaip-eligibility" element={<VbaipEligibility />} />
            <Route path="/services" element={<Services />} />
            <Route path="/job-preparation" element={<JobPreparation />} />
            <Route path="/learning-library" element={<LearningLibrary />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/dashboard" element={<Suspense fallback={<PageLoader />}><ProtectedRoute><Dashboard /></ProtectedRoute></Suspense>} />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/dental-ai" element={<DentalAI />} />
            <Route path="/summer-camp" element={<SummerCamp />} />
            <Route path="/sitemap" element={<SitemapPage />} />
            <Route path="/digital-marketing-gwalior" element={<DigitalMarketingGwalior />} />
            <Route path="/course-packages" element={<CoursePackages />} />
            <Route path="/prompt-library" element={<PromptLibrary />} />
            <Route path="/thumbnail-generator" element={<ThumbnailGenerator />} />
            <Route path="/blog-manage" element={<Suspense fallback={<PageLoader />}><ProtectedRoute><BlogManage /></ProtectedRoute></Suspense>} />
            <Route path="/blog-editor" element={<Suspense fallback={<PageLoader />}><ProtectedRoute><BlogEditor /></ProtectedRoute></Suspense>} />
            <Route path="/blog-editor/:id" element={<Suspense fallback={<PageLoader />}><ProtectedRoute><BlogEditor /></ProtectedRoute></Suspense>} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
